import { CheckCircle2, CloudOff, FileText, Link2Off } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { INTERVIEW_MODE_LABELS, submitProblems, type ScorecardDraft, type ScorecardLinkView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { SkeletonLines } from '../../../components/Skeleton';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { formatDateTime } from '../../../lib/format';
import { useNow } from '../../../lib/use-now';
import { scorecardLinkApi, useScorecardLink } from '../api/scorecards-api';
import { ScorecardForm } from '../components/ScorecardForm';
import styles from './ScorecardLink.module.css';

const CHECK_EVERY_MS = 15_000;
/** The device keeps what was typed, so a phone that closes the tab loses nothing (06-qol §4). */
const DRAFT_PREFIX = 'nr.scorecard-link.';

/**
 * INT-04 — a panelist's scorecard by their own link (C6, ADR 0077): no account, no sign-in. Built at
 * 375px first, as the cockpit is. Shows only what a panelist needs (§L23 "Scorecard link"): who, the
 * role, the round, when, where, and the CV. One mark from 1 to 5 and a short comment, submitted once;
 * the link then closes. Times show in the device's zone, with its abbreviation.
 */
export function ScorecardLinkPage({ token }: { readonly token: string }): ReactNode {
  const query = useScorecardLink(token);
  const [isDone, setIsDone] = useState(false);

  if (token === '') return <Notice icon={Link2Off} title="This link doesn't work" body="Use the link in the latest email we sent you." />;
  if (isDone) return <Notice icon={CheckCircle2} title="Thank you — your mark is in" body="HR has it now. This link has done its job, so you can close this page." />;
  if (query.isPending) {
    return (
      <Frame>
        <SkeletonLines count={6} />
      </Frame>
    );
  }
  if (query.error) {
    if (query.error.code === 'link_closed') return <Notice icon={query.error.details.reason === 'submitted' ? CheckCircle2 : Link2Off} title={query.error.details.reason === 'submitted' ? 'Your mark is in' : 'This link no longer works'} body={query.error.message} />;
    if (query.error.status === 404) return <Notice icon={Link2Off} title="This link doesn't work" body={query.error.message} />;
    return <Notice icon={CloudOff} title="We couldn't load your scorecard" body="Check your connection and try again." action={<Button variant="solid" onClick={() => void query.refetch()}>Try again</Button>} />;
  }
  return <LinkScorecard token={token} view={query.data} onDone={() => setIsDone(true)} />;
}

function LinkScorecard({ token, view, onDone }: { readonly token: string; readonly view: ScorecardLinkView; readonly onDone: () => void }): ReactNode {
  const storageKey = `${DRAFT_PREFIX}${token.slice(0, 16)}`;
  const [draft, setDraft] = useState<ScorecardDraft>(() => restored(storageKey) ?? { criteria: [], recommendation: null, notes: view.draft.comment, overallMark: view.draft.overallMark });
  const [isAttempted, setIsAttempted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [cvProblem, setCvProblem] = useState<string | null>(null);
  const now = useNow(CHECK_EVERY_MS);
  const opensAt = view.submitFrom ? new Date(view.submitFrom).getTime() : null;
  const isBeforeStart = opensAt !== null && now < opensAt;
  const problems = submitProblems(draft, false);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ overallMark: draft.overallMark, notes: draft.notes }));
    } catch {
      // Private browsing or a full device: the page still works, it just can't keep a copy.
    }
  }, [storageKey, draft.overallMark, draft.notes]);

  async function submit(): Promise<void> {
    setIsAttempted(true);
    if (problems.length > 0 || draft.overallMark === null) return;
    setIsSending(true);
    setProblem(null);
    try {
      await scorecardLinkApi.submit(token, { overallMark: draft.overallMark, comment: draft.notes.trim() });
      forget(storageKey);
      onDone();
    } catch (error) {
      setProblem(error instanceof ApiError && error.code === 'link_closed' ? error.message : `${errorMessage(error)} What you wrote is kept on this device.`);
    } finally {
      setIsSending(false);
    }
  }

  async function openCv(): Promise<void> {
    setCvProblem(null);
    try {
      await scorecardLinkApi.openCv(token);
    } catch (error) {
      setCvProblem(errorMessage(error));
    }
  }

  const blocked = view.submitBlockedReason ?? (isBeforeStart && view.submitFrom ? `You can give your mark once the interview starts, ${formatDateTime(view.submitFrom)}.` : null);

  return (
    <Frame>
      <p className={styles.company}>{view.companyName}</p>
      <h1 className={styles.title}>Your mark for {view.candidateName}</h1>
      <p className={styles.lead}>
        Hello {view.panelistName}. {view.jobTitle} · {view.roundName}
      </p>
      <dl className={styles.facts}>
        <dt>When</dt>
        <dd>{view.startsAt ? formatDateTime(view.startsAt) : '—'}</dd>
        <dt>Where</dt>
        <dd>
          {view.meetingLink ? (
            <a href={view.meetingLink} className={styles.link} target="_blank" rel="noreferrer">
              {INTERVIEW_MODE_LABELS[view.mode]} — join the call
            </a>
          ) : (
            [INTERVIEW_MODE_LABELS[view.mode], view.roomName].filter(Boolean).join(' · ')
          )}
        </dd>
        {view.cv && (
          <>
            <dt>CV</dt>
            <dd>
              <Button variant="outline" size="sm" icon={FileText} onClick={() => void openCv()}>
                Open the CV
              </Button>
              {cvProblem && <span className={styles.warning}>{cvProblem}</span>}
            </dd>
          </>
        )}
      </dl>

      <ScorecardForm draft={draft} onChange={setDraft} isReadOnly={isSending} isObserver={false} isMarkMissing={isAttempted && draft.overallMark === null} isCommentMissing={isAttempted && draft.notes.trim() === ''} guide={view.guide} />

      {/* #9: the others' marks never reach this page — it shows only yours. */}
      <p className={styles.quiet}>The other panelists can't see your mark, and you won't see theirs here.</p>
      {isAttempted && problems.length > 0 && (
        <p className={styles.warning} role="alert">
          {problems[0]}
        </p>
      )}
      {problem && (
        <p className={styles.warning} role="alert">
          {problem}
        </p>
      )}
      {blocked && <p className={styles.quiet}>{blocked}</p>}
      <Button variant="solid" size="lg" block isPending={isSending} disabled={blocked !== null} onClick={() => void submit()}>
        Submit my mark
      </Button>
      {view.expiresAt && <p className={styles.quiet}>This link works until you submit, or until {formatDateTime(view.expiresAt)}.</p>}
    </Frame>
  );
}

function restored(key: string): ScorecardDraft | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const mark = 'overallMark' in parsed && typeof parsed.overallMark === 'number' ? parsed.overallMark : null;
    const notes = 'notes' in parsed && typeof parsed.notes === 'string' ? parsed.notes : '';
    return { criteria: [], recommendation: null, notes, overallMark: mark };
  } catch {
    return null;
  }
}

function forget(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to forget on a device that kept nothing.
  }
}

function Frame({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <main className={styles.page}>
      <div className={styles.card}>{children}</div>
    </main>
  );
}

function Notice({ icon: Icon, title, body, action }: { readonly icon: typeof Link2Off; readonly title: string; readonly body: string; readonly action?: ReactNode }): ReactNode {
  return (
    <Frame>
      <Icon size={28} aria-hidden />
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.lead}>{body}</p>
      {action}
    </Frame>
  );
}
